import { AfterViewInit, Component, ElementRef, ViewChild } from '@angular/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import type { ReportStatistics } from '../../models/end-client-reporting-package';
import type { WeightCandidate } from './crosstab-stats';
import type { PickableVariable } from './report-aggregation';
import type { PartVariable } from './report-design-edit';
import type { WeightingDiagnostics } from './weighting';

/**
 * The report's statistics in a dialog: weighting, confidence, correction, small bases and decimals.
 * Opened from the top bar and from a crosstab's settings -- one place to set them, reachable
 * from where the question comes up, and plainly report level rather than one table's.
 *
 * The deck stays the owner. It hands over readers rather than values, so the dialog redraws as each
 * change is applied -- the targets' achieved shares and the diagnostics move as figures are typed.
 */
@Component({
  selector: 'app-report-statistics-modal',
  standalone: false,
  template: `
    <div class="stats-modal">
      <header class="stats-modal-head">
        <div>
          <h2 id="report-statistics-title" class="stats-modal-title mb-0">Report statistics</h2>
          <p class="stats-modal-sub mb-0">
            <i class="fas fa-layer-group mr-1" aria-hidden="true"></i>Applies to every module in this report.
          </p>
        </div>
        <button #closeButton type="button" class="stats-modal-close ml-auto" aria-label="Close report statistics" (click)="close()">
          <i class="fas fa-xmark" aria-hidden="true"></i>
        </button>
      </header>

      <div class="stats-modal-body">
        <app-report-statistics
          [statistics]="statistics()"
          [diagnostics]="diagnostics()"
          [weightColumns]="weightColumns()"
          [variables]="variables()"
          [sharesOf]="sharesOf"
          (statisticsChange)="change($event)"
          (downloadWeights)="downloadWeights()">
        </app-report-statistics>
      </div>

      <footer class="stats-modal-foot">
        @if (standalone) {
          @if (failed()) {
            <span class="stats-modal-note is-error" role="alert">
              <i class="fas fa-triangle-exclamation mr-1" aria-hidden="true"></i>Couldn't save. Try again, or Cancel to put things back.
            </span>
          } @else {
            <span class="stats-modal-note">The report previews your changes. Nothing is kept until you save.</span>
          }
          <button type="button" class="btn btn-sm btn-link nav-font12 ml-auto" [disabled]="saving()" (click)="close()">Cancel</button>
          <button type="button" class="btn btn-sm btn-primary" [disabled]="saving() || !changed()" (click)="onSave()">
            {{ saving() ? 'Saving…' : 'Save' }}
          </button>
        } @else {
          <span class="stats-modal-note">Kept with your other edits when you choose Save changes.</span>
          <button type="button" class="btn btn-sm btn-primary ml-auto" (click)="close()">Done</button>
        }
      </footer>
    </div>
  `,
  styles: [`
    :host { display: block; }
    .stats-modal { display: flex; flex-direction: column; max-height: 88vh; }
    .stats-modal-head {
      display: flex; align-items: center; padding: 16px 20px;
      border-bottom: 1px solid var(--nav-grey1);
    }
    .stats-modal-title { font-size: var(--nav-font16); font-weight: 700; color: var(--nav-grey6); }
    .stats-modal-sub { margin-top: 2px; font-size: var(--nav-font12); color: var(--nav-grey4); }
    .stats-modal-close {
      width: 32px; height: 32px; border: 0; border-radius: 50%;
      color: var(--nav-grey4); background: transparent;
    }
    .stats-modal-close:hover, .stats-modal-close:focus-visible { color: var(--nav-grey6); background: var(--nav-snow-white); }
    /* Anything wider than the dialog -- a long target table -- scrolls inside it, never the page. */
    .stats-modal-body { flex: 1 1 auto; min-height: 0; min-width: 0; overflow: auto; padding: 16px 20px; }
    .stats-modal-foot {
      display: flex; flex-wrap: wrap; align-items: center; gap: 12px; padding: 10px 20px;
      border-top: 1px solid var(--nav-grey1);
    }
    .stats-modal-note { font-size: var(--nav-font12); color: var(--nav-grey4); }
    .stats-modal-note.is-error { color: var(--nav-error); }
    /* A phone: the dialog is the whole screen (ecr-stats-dialog), so this fills it, with the head and
       foot fixed and the body between them scrolling. The note takes a line of its own over the
       buttons rather than squeezing them. */
    @media (max-width: 575.98px) {
      .stats-modal { height: 100vh; height: 100dvh; max-height: none; }
      .stats-modal-head, .stats-modal-body { padding-left: 16px; padding-right: 16px; }
      .stats-modal-foot { padding: 10px 16px; }
      .stats-modal-note { flex: 1 1 100%; }
    }
  `],
})
export class ReportStatisticsModalComponent implements AfterViewInit {
  // Readers from the deck, set through initialState before the first render.
  statistics: () => ReportStatistics | undefined = () => undefined;
  diagnostics: () => WeightingDiagnostics | null = () => null;
  weightColumns: () => WeightCandidate[] = () => [];
  variables: () => PickableVariable[] = () => [];
  sharesOf: (variableKey: string) => { answer: string; percent: number }[] = () => [];
  onChange: (change: { statistics: ReportStatistics; variables: PartVariable[] }) => void = () => undefined;
  onDownloadWeights: () => void = () => undefined;

  /**
   * Opened while viewing rather than editing: it then has its own Save and Cancel, and the deck puts
   * the changes back however it closes unsaved. While editing, the edit's Save changes keeps them.
   */
  standalone = false;
  changed: () => boolean = () => false;
  saving: () => boolean = () => false;
  failed: () => boolean = () => false;
  onSave: () => void = () => undefined;

  @ViewChild('closeButton') private closeButton?: ElementRef<HTMLButtonElement>;

  constructor(private modalRef: BsModalRef) {}

  ngAfterViewInit() {
    // Focus inside the dialog, on something harmless, so Tab starts from the top of it.
    setTimeout(() => this.closeButton?.nativeElement.focus());
  }

  change(change: { statistics: ReportStatistics; variables: PartVariable[] }) {
    this.onChange(change);
  }

  downloadWeights() {
    this.onDownloadWeights();
  }

  close() {
    this.modalRef.hide();
  }
}
